/* eslint-disable max-lines */
import { useCallback, useState } from "react";
import {
  trackDtfAiRedrawNoteSubmitted,
  trackDtfAiRedrawQuestionsAnswered,
  trackDtfAiRedrawInterpretationComplete,
  trackDtfAiRedrawInterpretationFailed
} from "@design-lab/shared/lib/analytics";
import { useCreateLlmRedrawRequest, useUpdateLlmRedrawRequest } from "../api/useLlmRedrawRequests";
import type {
  Question,
  ArtistInstructionsState,
  ArtistInstructionsArtwork,
  LlmInterpretationState
} from "./artist-instructions-types";
import { ARTIST_INSTRUCTIONS_STATES } from "./artist-instructions-types";

interface UseArtistInstructionsActionsProps {
  artistInstructionsArtwork: ArtistInstructionsArtwork;
  instructions: string;
  questions: Question[];
  setQuestions: (questions: Question[]) => void;
  currentState: ArtistInstructionsState;
  setCurrentState: (state: ArtistInstructionsState) => void;
  llmRedrawRequestId: number | null;
  setLlmRedrawRequestId: (id: number | null) => void;
  handleArtistInstructionsStateChange: (data: {
    llmInterpretationState: LlmInterpretationState;
    enhancementNote?: string;
    llmRedrawRequestId?: number;
  }) => void;
}

export function useArtistInstructionsActions({
  artistInstructionsArtwork,
  instructions,
  questions,
  setQuestions,
  currentState,
  setCurrentState,
  llmRedrawRequestId,
  setLlmRedrawRequestId,
  handleArtistInstructionsStateChange
}: UseArtistInstructionsActionsProps) {
  const createLlmRedrawRequest = useCreateLlmRedrawRequest(artistInstructionsArtwork.id);
  const updateLlmRedrawRequest = useUpdateLlmRedrawRequest(artistInstructionsArtwork.id);
  const [hasError, setHasError] = useState(false);

  const createRequestInProgress = createLlmRedrawRequest.isPending;
  const updateRequestInProgress = updateLlmRedrawRequest.isPending;

  const handleSubmit = useCallback(async () => {
    setHasError(false);

    try {
      if (
        currentState === ARTIST_INSTRUCTIONS_STATES.INITIAL ||
        currentState === ARTIST_INSTRUCTIONS_STATES.TYPING
      ) {
        // Create new request
        trackDtfAiRedrawNoteSubmitted(artistInstructionsArtwork.id, instructions);

        const response = await createLlmRedrawRequest.mutateAsync({
          dtfArtworkId: artistInstructionsArtwork.id,
          initialPrompt: instructions
        });

        if (response.clarifyingQuestions && response.clarifyingQuestions.length > 0) {
          const mappedQuestions: Question[] = response.clarifyingQuestions.map(q => ({
            title: q.question || q.text || q.title || "",
            options: q.options || [],
            answer: undefined
          }));

          setQuestions(mappedQuestions);
          setCurrentState(ARTIST_INSTRUCTIONS_STATES.QUESTIONS);
          setLlmRedrawRequestId(response.id);

          handleArtistInstructionsStateChange({
            llmInterpretationState: {
              currentState: ARTIST_INSTRUCTIONS_STATES.QUESTIONS,
              instructions,
              questions: mappedQuestions,
              previousState: currentState,
              previousInstructions: instructions,
              previousQuestions: [],
              llmRedrawRequestId: response.id
            },
            llmRedrawRequestId: response.id
          });
        } else {
          // No questions, go directly to final state
          setCurrentState(ARTIST_INSTRUCTIONS_STATES.FINAL);
          setLlmRedrawRequestId(response.id);

          handleArtistInstructionsStateChange({
            llmInterpretationState: {
              currentState: ARTIST_INSTRUCTIONS_STATES.FINAL,
              instructions,
              questions: [],
              previousState: currentState,
              previousInstructions: instructions,
              previousQuestions: [],
              llmRedrawRequestId: response.id
            },
            enhancementNote: instructions,
            llmRedrawRequestId: response.id
          });

          trackDtfAiRedrawInterpretationComplete(
            artistInstructionsArtwork.id,
            "initial_submission_no_questions"
          );
        }
      } else if (currentState === ARTIST_INSTRUCTIONS_STATES.QUESTIONS && llmRedrawRequestId) {
        // Update with answers
        const answersMap = questions.reduce(
          (acc, q) => {
            if (q.answer) {
              acc[q.title] = q.answer;
            }
            return acc;
          },
          {} as Record<string, string>
        );

        trackDtfAiRedrawQuestionsAnswered(artistInstructionsArtwork.id, answersMap);

        await updateLlmRedrawRequest.mutateAsync({
          id: llmRedrawRequestId,
          followUpQuestionsAndResponses: answersMap
        });

        setCurrentState(ARTIST_INSTRUCTIONS_STATES.FINAL);

        handleArtistInstructionsStateChange({
          llmInterpretationState: {
            currentState: ARTIST_INSTRUCTIONS_STATES.FINAL,
            instructions,
            questions,
            previousState: currentState,
            previousInstructions: instructions,
            previousQuestions: questions,
            llmRedrawRequestId
          },
          enhancementNote: instructions,
          llmRedrawRequestId
        });

        trackDtfAiRedrawInterpretationComplete(artistInstructionsArtwork.id, "questions_answered");
      }
    } catch (error) {
      console.error("Error processing artist instructions:", error);
      window.Honeybadger?.notify("Error processing artist instructions", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          hook: "useArtistInstructionsActions",
          artworkId: artistInstructionsArtwork.id,
          currentState
        }
      });
      setHasError(true);

      trackDtfAiRedrawInterpretationFailed(artistInstructionsArtwork.id, currentState);
    }
  }, [
    artistInstructionsArtwork.id,
    instructions,
    questions,
    currentState,
    llmRedrawRequestId,
    createLlmRedrawRequest,
    updateLlmRedrawRequest,
    setQuestions,
    setCurrentState,
    setLlmRedrawRequestId,
    handleArtistInstructionsStateChange
  ]);

  return {
    handleSubmit,
    createRequestInProgress,
    updateRequestInProgress,
    hasError
  };
}
